import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { BiCarousel } from "react-icons/bi";
import { MdOutlineFileDownload, MdOutlineFeedback } from "react-icons/md";
import { RiEditLine, RiShareForwardLine } from "react-icons/ri";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const InstaPostViewDialog = ({
  open,
  onClose,
  instaUrl = "",
  instaPostId,
  onStartRegenerate,
  onDownload,
}) => {
  const [alertInfo, setAlertInfo] = useState(null);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComments, setFeedbackComments] = useState("");
  const [feedbackConsent, setFeedbackConsent] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [previousFeedbacks, setPreviousFeedbacks] = useState([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(false);
  const token = localStorage.getItem("authToken");



  const handleComingSoon = () => {
    setAlertInfo({ severity: "info", message: "Coming soon" });
  };

  const handleCloseAlert = () => {
    setAlertInfo(null);
  };

  const handleOpenFeedback = () => {
    setFeedbackRating(0);
    setFeedbackComments("");
    setFeedbackConsent(false);
    setFeedbackDialogOpen(true);
    fetchPreviousFeedbacks();
  };

  const fetchPreviousFeedbacks = async () => {
    setLoadingFeedbacks(true);
    try {
      const url = `${BASE_URL}/project_feedbacks?id=${instaPostId}&type=instapost&version=1`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setPreviousFeedbacks(data.feedbacks || []);
      } else {
        console.error("Failed to fetch previous feedbacks:", response.status);
        setPreviousFeedbacks([]);
      }
    } catch (error) {
      console.error("Error fetching previous feedbacks:", error);
      setPreviousFeedbacks([]);
    } finally {
      setLoadingFeedbacks(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (feedbackRating === 0) {
      setAlertInfo({ severity: "warning", message: "Please select a rating" });
      return;
    }

    setSubmittingFeedback(true);
    try {
      const formData = new FormData();
      formData.append("stars", feedbackRating);
      formData.append("comments", feedbackComments);
      formData.append("id", instaPostId || "");
      formData.append("type", "instapost");
      formData.append("version", 1);

      const response = await fetch(`${BASE_URL}/submit_feedback`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        setAlertInfo({ severity: "success", message: "Feedback submitted successfully!" });
        setFeedbackDialogOpen(false);
        setFeedbackRating(0);
        setFeedbackComments("");
        setFeedbackConsent(false);
      } else {
        console.error("[InstaPostViewDialog] Feedback submission failed:", response.status);
        setAlertInfo({ severity: "error", message: "Failed to submit feedback" });
      }
    } catch (error) {
      console.error("[InstaPostViewDialog] Error submitting feedback:", error);
      setAlertInfo({ severity: "error", message: "Error submitting feedback" });
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const ratingOptions = [
    { value: 1, label: "Awful", emoji: "😣" },
    { value: 2, label: "Poor", emoji: "😞" },
    { value: 3, label: "Neutral", emoji: "😐" },
    { value: 4, label: "Good", emoji: "🤩" },
    { value: 5, label: "Excellent", emoji: "😍" },
  ];


  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          style: {
            width: "900px",
            maxWidth: "900px",
            borderRadius: "8px",
            overflow: "visible",
            fontFamily: "'Inter', sans-serif",
          },
        }}
        sx={{
          "& *": { fontFamily: "'Inter', sans-serif !important" },
        }}
      >
        <IconButton
          style={{ position: "absolute", top: 8, right: 8, color: "#838383" }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle style={{ padding: "16px", borderBottom: "1px solid #eee" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <BiCarousel style={{ color: "#1E4DE3", fontSize: "20px" }} />
            </div>

            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                gap: "8px",
                marginRight: "48px",
              }}
            >
              <Tooltip title="Coming soon">
                <Button
                  variant="contained"
                  startIcon={<RiEditLine style={{ color: "#000" }} />}
                  onClick={handleComingSoon}
                  style={{
                    backgroundColor: "#d3d3d3",
                    color: "#000",
                    textTransform: "none",
                    boxShadow: "none",
                    cursor: "not-allowed",
                  }}
                >
                  Edit
                </Button>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<MdOutlineFileDownload style={{ color: "#1E4DE3" }} />}
                onClick={onDownload}
                sx={{
                  borderColor: "#d3d3d3",
                  color: "#1E4DE3",
                  textTransform: "none",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    borderColor: "#1E4DE3",
                    boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                    backgroundColor: "#e8f0fe",
                  },
                }}
              >
                Download
              </Button>
              <Tooltip title="Coming soon">
                <Button
                  variant="text"
                  startIcon={<RiShareForwardLine style={{ color: "#000" }} />}
                  onClick={handleComingSoon}
                  style={{
                    backgroundColor: "#d3d3d3",
                    color: "#000",
                    textTransform: "none",
                    boxShadow: "none",
                    cursor: "not-allowed",
                  }}
                >
                  Share
                </Button>
              </Tooltip>

              <Button
                variant="outlined"
                startIcon={<MdOutlineFeedback style={{ color: "#1E4DE3" }} />}
                onClick={handleOpenFeedback}
                sx={{
                  borderColor: "#d3d3d3",
                  color: "#1E4DE3",
                  textTransform: "none",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    borderColor: "#1E4DE3",
                    boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                    backgroundColor: "#e8f0fe",
                  },
                }}
              >
                Feedback
              </Button>
            </div>
          </div>
        </DialogTitle>
        <DialogContent style={{ padding: "16px" }}>
          <div
            style={{
              position: "relative",
              width: "100%",
              paddingTop: "56.25%",
              backgroundColor: "#000",
              marginBottom: "16px",
            }}
          >
            {instaUrl ? (
              // ✅ Render iframe at 2× dimensions then scale down via CSS transform.
              // This forces Office Online to rasterize at double resolution, eliminating
              // the blurriness caused by its default low-DPI rendering.
              (() => {
                const SCALE = 2;
                return (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <iframe
                      key={instaUrl}
                      src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                        instaUrl
                      )}`}
                      style={{
                        border: "none",
                        // Render at 2× logical size
                        width: `${100 * SCALE}%`,
                        height: `${100 * SCALE}%`,
                        // Scale back down to fit — origin top-left keeps it anchored correctly
                        transform: `scale(${1 / SCALE})`,
                        transformOrigin: "top left",
                      }}
                      title="Instapost Viewer"
                    />
                  </div>
                );
              })()
            ) : (
              <p
                style={{
                  color: "#fff",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                No Instapost available
              </p>
            )}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Coming soon">
              <Button
                variant="contained"
                startIcon={<BiCarousel style={{ color: "#000" }} />}
                onClick={handleComingSoon}
                style={{
                  backgroundColor: "#d3d3d3",
                  color: "#000",
                  textTransform: "none",
                  boxShadow: "none",
                  cursor: "not-allowed",
                }}
              >
                Regenerate
              </Button>
            </Tooltip>
          </div>
        </DialogContent>
      </Dialog>

      {/* Snackbar for alerts - displayed outside the dialog */}
      <Snackbar
        open={!!alertInfo}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alertInfo?.severity || "info"}
          sx={{ width: '100%' }}
        >
          {alertInfo?.message}
        </Alert>
      </Snackbar>

      {/* Feedback Dialog */}
      <Dialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              padding: "16px",
              minWidth: 400,
              maxWidth: 450,
              maxHeight: "90vh",
            },
          },
        }}
      >
        <DialogTitle sx={{
          padding: "0 0 16px 0",
          fontWeight: 600,
          fontSize: "18px",
        }}>
          Help us improve our product for you
        </DialogTitle>
        <DialogContent sx={{ padding: 0, overflowY: "auto" }}>
          {loadingFeedbacks ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "20px" }}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <>
              {/* Previous Reviews Section */}
              {previousFeedbacks.length > 0 && (
                <Box sx={{ marginBottom: "24px" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, marginBottom: "12px", color: "#333" }}
                  >
                    Previous Reviews
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "8px",
                    }}
                  >
                    {previousFeedbacks.map((feedback, index) => {
                      const matchedOption = ratingOptions.find(
                        (opt) => opt.value === feedback.stars
                      ) || { emoji: "❓", label: "Unknown" };
                      return (
                        <Box
                          key={index}
                          sx={{
                            padding: "12px",
                            marginBottom: "8px",
                            backgroundColor: "#f9f9f9",
                            borderRadius: "6px",
                            "&:last-child": { marginBottom: 0 },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "6px",
                            }}
                          >
                            <span style={{ fontSize: "24px", marginRight: "8px" }}>
                              {matchedOption.emoji}
                            </span>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "#333" }}
                            >
                              {matchedOption.label}
                            </Typography>
                          </Box>
                          {feedback.comments && (
                            <Typography
                              variant="body2"
                              sx={{ color: "#555", marginBottom: "4px" }}
                            >
                              {feedback.comments}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: "#999" }}>
                            {new Date(feedback.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Add New Review Section */}
              {previousFeedbacks.length > 0 && (
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, marginBottom: "12px", color: "#333" }}
                >
                  Add New Review
                </Typography>
              )}

              <p style={{ margin: "0 0 16px 0", color: "#666", fontSize: "14px" }}>
                {previousFeedbacks.length > 0
                  ? "Share your latest thoughts with us:"
                  : "How would you like to describe your experience with the app today?"}
              </p>

              {/* Rating Emojis */}
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "20px",
              }}>
                {ratingOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => setFeedbackRating(option.value)}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      cursor: "pointer",
                      padding: "8px",
                      borderRadius: "8px",
                      backgroundColor: feedbackRating === option.value ? "#e8f0fe" : "transparent",
                      border: feedbackRating === option.value ? "2px solid #1E4DE3" : "2px solid transparent",
                      transition: "all 0.2s",
                    }}
                  >
                    <span style={{ fontSize: "32px" }}>{option.emoji}</span>
                    <span style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>{option.label}</span>
                  </div>
                ))}
              </div>

              {/* Comments TextField */}
              <TextField
                multiline
                rows={4}
                placeholder="Tell us more about your experience (optional)"
                value={feedbackComments}
                onChange={(e) => setFeedbackComments(e.target.value.slice(0, 500))}
                fullWidth
                sx={{
                  marginBottom: "8px",
                  "& .MuiOutlinedInput-root": {
                    fontSize: "14px",
                  },
                }}
              />
              <p style={{ margin: "0 0 16px 0", color: "#999", fontSize: "12px" }}>
                {500 - feedbackComments.length}/500 characters left
              </p>

              {/* Consent Checkbox */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={feedbackConsent}
                    onChange={(e) => setFeedbackConsent(e.target.checked)}
                    sx={{ color: "#1E4DE3", "&.Mui-checked": { color: "#1E4DE3" } }}
                  />
                }
                label={
                  <span style={{ fontSize: "13px", color: "#666" }}>
                    I consent to PubVision AI contacting me based on my feedback
                  </span>
                }
              />

              {/* Buttons */}
              <div style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                marginTop: "20px",
              }}>
                <Button
                  variant="outlined"
                  onClick={() => setFeedbackDialogOpen(false)}
                  sx={{
                    textTransform: "none",
                    borderColor: "#ccc",
                    color: "#333",
                    "&:hover": {
                      borderColor: "#999",
                    },
                  }}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmitFeedback}
                  disabled={submittingFeedback || feedbackRating === 0}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#1E4DE3",
                    "&:hover": {
                      backgroundColor: "#1a3fb8",
                    },
                  }}
                >
                  {submittingFeedback ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Submit feedback"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
InstaPostViewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  instaUrl: PropTypes.string,
  instaPostId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onStartRegenerate: PropTypes.func,
  onDownload: PropTypes.func,
};

export default InstaPostViewDialog;
